import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase-lazy";
import {
  clearSessionCookie,
  readSessionCookie,
  writeSessionCookie,
} from "@/lib/cross-domain-session";

/**
 * Shared auth state singleton.
 *
 * Why: previously every component (Header, Footer, InternalFooter, ProtectedRoute,
 * SuccessStoriesTab, etc.) called `supabase.auth.getSession()` and registered its
 * own `onAuthStateChange` listener at mount. On a single landing render this
 * triggered ~6 concurrent `LockManager` requests against
 * `lock:sb-...-auth-token`, producing the 5s "lock not released" warnings and
 * the visible flicker (false logged-out → logged-in flip) while sessions
 * settled.
 *
 * This module performs ONE getSession() and ONE onAuthStateChange subscription
 * for the whole app. Components subscribe to the in-memory state via React.
 */

type Listener = (s: AuthSnapshot) => void;

export interface AuthSnapshot {
  session: Session | null;
  user: User | null;
  isAuthenticated: boolean;
  isReady: boolean;
  authIssue?: "timeout" | null;
}

let snapshot: AuthSnapshot = {
  session: null,
  user: null,
  isAuthenticated: false,
  isReady: false,
  authIssue: null,
};

export const isValidJwt = (t: unknown): t is string =>
  typeof t === "string" && t.split(".").length === 3;

const listeners = new Set<Listener>();
let initialized = false;

const AUTH_TIMEOUT_MS = 7_000;

const withAuthTimeout = async <T,>(promise: Promise<T>, label: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(`${label}_timeout`)), AUTH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const setSnapshot = (next: Partial<AuthSnapshot>) => {
  snapshot = { ...snapshot, ...next };
  if (snapshot.isReady) {
    void import("@/lib/gaIdentity").then((m) => m.syncGaIdentity(snapshot.user?.id ?? null)).catch(() => {});
  }
  listeners.forEach((l) => l(snapshot));
};

const attributeMicroDiagnostico = async (userId: string) => {
  if (typeof window === "undefined") return;
  try {
    const supabase = await getSupabase();
    const sessionId = window.localStorage.getItem("micro_diag_session");
    const pendingSector = window.localStorage.getItem("micro_diag_pending_sector");

    if (sessionId) {
      const { error } = await supabase.rpc("mark_diagnostico_signup", { _session_id: sessionId });
      if (!error) {
        window.localStorage.removeItem("micro_diag_session");
      }
    }

    if (pendingSector) {
      // Only fill if currently empty (don't overwrite user's explicit choice)
      const { data: profile } = await supabase
        .from("profiles")
        .select("retail_sector")
        .eq("user_id", userId)
        .maybeSingle();
      if (profile && (!profile.retail_sector || profile.retail_sector === "")) {
        await supabase
          .from("profiles")
          .update({ retail_sector: pendingSector })
          .eq("user_id", userId);
      }
      window.localStorage.removeItem("micro_diag_pending_sector");
    }
  } catch {
    /* best-effort attribution; never block auth */
  }
};

const init = () => {
  if (initialized) return;
  if (typeof window === "undefined") return;
  initialized = true;
  void getSupabase().then((supabase) => initWithClient(supabase));
};

const initWithClient = (supabase: Awaited<ReturnType<typeof getSupabase>>) => {
  // Auth state listener FIRST (synchronous handler — no await, no DB calls
  // here, to avoid holding the gotrue lock).
  supabase.auth.onAuthStateChange((event, session) => {
    if (session && !isValidJwt(session.access_token)) {
      setSnapshot({
        session: null,
        user: null,
        isAuthenticated: false,
        isReady: true,
        authIssue: null,
      });
      clearSessionCookie();
      setTimeout(() => {
        void supabase.auth.signOut({ scope: "local" }).catch(() => undefined);
      }, 0);
      return;
    }

    setSnapshot({
      session,
      user: session?.user ?? null,
      isAuthenticated: !!session,
      isReady: true,
      authIssue: null,
    });
    // Mirror session into a `.retaillemon.com` cookie so sibling subdomains
    // (panel ↔ marketing) stay in sync.
    // IMPORTANT: only clear on explicit SIGNED_OUT — INITIAL_SESSION may fire
    // with a null session on the public domain before getSession() has had a
    // chance to hydrate from the cross-subdomain cookie. Clearing it here
    // would destroy the bridge from panel.retaillemon.com.
    if (event === "SIGNED_OUT") {
      clearSessionCookie();
    } else if (
      session &&
      (event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED" ||
        event === "INITIAL_SESSION")
    ) {
      writeSessionCookie(session);
    }
    // Defer DB work to avoid holding the gotrue lock
    if (event === "SIGNED_IN" && session?.user?.id) {
      const uid = session.user.id;
      setTimeout(() => { void attributeMicroDiagnostico(uid); }, 0);
    }
  });

  withAuthTimeout(supabase.auth.getSession(), "auth_get_session")
    .then(async ({ data: { session } }) => {
      let finalSession = session;

      // Guard: a JWT must have exactly 3 segments. If localStorage holds a
      // corrupted token (observed in production as "token contains an invalid
      // number of segments" 403s against /auth/v1/user), nuke it so the user
      // lands on the public surface instead of a wedged Dashboard.
      if (finalSession && !isValidJwt(finalSession.access_token)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* noop */ }
        clearSessionCookie();
        finalSession = null;
      }

      // Cross-subdomain hydration: if local has no session but the parent-domain
      // cookie carries one (set by panel.retaillemon.com), restore it here.
      if (!finalSession) {
        const cookieSession = readSessionCookie();
        if (cookieSession && isValidJwt(cookieSession.access_token) && cookieSession.refresh_token) {
          const { data, error } = await withAuthTimeout(
            supabase.auth.setSession({
              access_token: cookieSession.access_token,
              refresh_token: cookieSession.refresh_token,
            }),
            "auth_set_session",
          );
          if (error || !data.session) {
            clearSessionCookie();
          } else {
            finalSession = data.session;
          }
        } else if (cookieSession) {
          // Cookie payload is malformed — drop it.
          clearSessionCookie();
        }
      }
      setSnapshot({
        session: finalSession,
        user: finalSession?.user ?? null,
        isAuthenticated: !!finalSession,
        isReady: true,
        authIssue: null,
      });
      if (finalSession) writeSessionCookie(finalSession);
      // Also attribute on hard refresh if a fresh session exists
      if (finalSession?.user?.id) {
        const uid = finalSession.user.id;
        setTimeout(() => { void attributeMicroDiagnostico(uid); }, 0);
      }
    })
    .catch(() => {
      // iOS/Safari can leave the Web Locks API in a zombie state after a PWA/tab
      // restore. Never keep the app in an infinite auth loader: expose a safe
      // unauthenticated state so pages can render their own recovery UI.
      if (snapshot.session && snapshot.isAuthenticated) {
        setSnapshot({ isReady: true, authIssue: null });
        return;
      }
      setSnapshot({ session: null, user: null, isAuthenticated: false, isReady: true, authIssue: "timeout" });
    });
};

// El primer render del cliente debe coincidir con el HTML del servidor: si se
// leyera el snapshot vivo (que puede estar ya resuelto antes de la hidratación)
// React descartaría el árbol por hydration mismatch.
const SSR_SNAPSHOT: AuthSnapshot = {
  session: null,
  user: null,
  isAuthenticated: false,
  isReady: false,
  authIssue: null,
};

export function useAuthState(): AuthSnapshot {
  const [state, setState] = useState<AuthSnapshot>(SSR_SNAPSHOT);

  useEffect(() => {
    listeners.add(setState);
    setState(snapshot);
    const path = window.location.pathname;
    const isPublicHome = path === "/" || path === "/en";
    if (!isPublicHome) {
      init();
      return () => {
        listeners.delete(setState);
      };
    }

    // En la portada distinguimos dos casos para no cargar el SDK de backend
    // (~53 KB) en visitas anónimas, que son la mayoría y las que mide
    // PageSpeed:
    //  - si hay pista de sesión (cookie de dominio padre o token guardado),
    //    resolvemos ya: el header pasa a "Ir al Panel" sin esperas.
    //  - si no hay pista, esperamos a la primera interacción real.
    const hasSessionHint = (): boolean => {
      try {
        if (readSessionCookie()) return true;
        for (let i = 0; i < window.localStorage.length; i += 1) {
          const key = window.localStorage.key(i);
          if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) return true;
        }
      } catch {
        /* almacenamiento bloqueado: tratamos como visita anónima */
      }
      return false;
    };

    if (hasSessionHint()) {
      init();
      return () => {
        listeners.delete(setState);
      };
    }

    const activate = () => init();
    const events = ["pointerdown", "keydown", "touchstart", "scroll", "wheel"] as const;
    events.forEach((event) => window.addEventListener(event, activate, { once: true, passive: true }));
    return () => {
      events.forEach((event) => window.removeEventListener(event, activate));
      listeners.delete(setState);
    };


  }, []);

  return state;
}

export function getAuthSnapshot(): AuthSnapshot {
  return snapshot;
}
